import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../services/notification.service';
import {
  Campaign,
  CampaignService,
  CampaignTrackingEvent,
  CampaignTrackingEventsFilters,
} from '../../services/campaign.service';
import { PhoneValidator } from '../../shared/validators/phone.validator';

interface LeadFilters {
  eventTypes: string[];
}

@Component({
  selector: 'app-campaign-leads',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './campaign-leads.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./campaign-leads.component.scss'],
})
export class CampaignLeadsComponent implements OnChanges {
  @Input() campaign!: Campaign;

  leads: CampaignTrackingEvent[] = [];
  filteredLeads: CampaignTrackingEvent[] = [];
  loading = false;
  exporting = false;
  error: string | null = null;
  searchQuery = '';
  showFilterPanel = false;

  page = 1;
  limit = 20;
  total = 0;
  /** Leads with phone/email only — used to enable Export, excludes unknown leads. */
  exportTotal = 0;

  readonly eventTypeOptions = ['clicked', 'offer_received', 'conversion'] as const;
  /** Default list/export types — all tracking events except failed. */
  private readonly defaultLeadEventTypes = [
    'opened',
    'clicked',
    'sent',
    'delivered',
    'impression',
    'conversion',
    'otp_send',
    'offer_received',
  ] as const;
  filters: LeadFilters = {
    eventTypes: [],
  };

  draftFilters: LeadFilters = { eventTypes: [] };

  readonly Math = Math;

  constructor(
    private campaignService: CampaignService,
    private messageService: NotificationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['campaign']?.currentValue?.id) {
      this.page = 1;
      this.loadLeads();
    }
  }

  loadLeads(): void {
    if (!this.campaign?.id) {
      return;
    }

    this.loading = true;
    this.error = null;

    const apiFilters: CampaignTrackingEventsFilters = {
      page: this.page,
      limit: this.limit,
      event_type: this.getApiEventTypeParam(),
    };

    this.campaignService.getCampaignTrackingEvents(this.campaign.id, apiFilters).subscribe({
      next: response => {
        if (!response.status) {
          this.error = response.message || 'Failed to load leads';
          this.leads = [];
          this.filteredLeads = [];
          this.total = 0;
          this.exportTotal = 0;
          this.loading = false;
          return;
        }

        let events = response.data?.events ?? [];
        events = this.applyEventTypeFilter(events);
        this.leads = events;
        const contactableTotal =
          response.data?.pagination?.total_with_contact ?? response.data?.pagination?.total ?? 0;
        this.total = contactableTotal;
        this.exportTotal = contactableTotal;
        this.limit = response.data?.pagination?.limit ?? this.limit;
        this.applySearchFilter();
        this.loading = false;
      },
      error: err => {
        this.error = err?.error?.message || 'Failed to load leads';
        this.leads = [];
        this.filteredLeads = [];
        this.total = 0;
        this.exportTotal = 0;
        this.loading = false;
      },
    });
  }

  onSearchChange(): void {
    this.applySearchFilter();
  }

  exportLeads(): void {
    if (!this.campaign?.id || this.exporting || this.loading || this.exportTotal === 0) {
      return;
    }

    this.exporting = true;
    const apiFilters: Omit<CampaignTrackingEventsFilters, 'page' | 'limit'> = {
      event_type: this.getApiEventTypeParam(),
    };

    this.campaignService.exportCampaignTrackingEvents(this.campaign.id, apiFilters).subscribe({
      next: blob => {
        this.downloadBlob(blob, this.buildExportFilename());
        this.exporting = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Export ready',
          detail: `Downloaded ${this.exportTotal} lead${this.exportTotal === 1 ? '' : 's'} with phone or email.`,
        });
      },
      error: error => {
        this.exporting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Export failed',
          detail:
            error?.status === 400
              ? 'Too many leads to export. Narrow your filters and try again.'
              : 'Unable to export leads. Please try again.',
        });
      },
    });
  }

  toggleFilterPanel(): void {
    if (!this.showFilterPanel) {
      this.draftFilters = { eventTypes: [...this.filters.eventTypes] };
    }
    this.showFilterPanel = !this.showFilterPanel;
  }

  toggleDraftEventType(eventType: string): void {
    const index = this.draftFilters.eventTypes.indexOf(eventType);
    if (index >= 0) {
      this.draftFilters.eventTypes = this.draftFilters.eventTypes.filter(
        type => type !== eventType
      );
    } else {
      this.draftFilters.eventTypes = [...this.draftFilters.eventTypes, eventType];
    }
  }

  isDraftEventTypeSelected(eventType: string): boolean {
    return this.draftFilters.eventTypes.includes(eventType);
  }

  canResetFilters(): boolean {
    return this.filters.eventTypes.length > 0 || this.draftFilters.eventTypes.length > 0;
  }

  applyFilters(): void {
    this.filters = { eventTypes: [...this.draftFilters.eventTypes] };
    this.page = 1;
    this.showFilterPanel = false;
    this.loadLeads();
  }

  resetFilters(): void {
    const hadAppliedFilters = this.filters.eventTypes.length > 0;
    this.draftFilters = { eventTypes: [] };

    if (hadAppliedFilters) {
      this.filters = { eventTypes: [] };
      this.page = 1;
      this.loadLeads();
    }
  }

  goToPreviousPage(): void {
    if (this.page > 1) {
      this.page -= 1;
      this.loadLeads();
    }
  }

  goToNextPage(): void {
    if (this.page * this.limit < this.total) {
      this.page += 1;
      this.loadLeads();
    }
  }

  getLeadPrimaryLabel(event: CampaignTrackingEvent): string {
    const customer = event.customer;
    if (customer?.contact?.trim()) {
      return this.formatPhoneNumber(customer.contact);
    }
    if (customer?.email?.trim()) {
      return customer.email;
    }
    const name = [customer?.first_name, customer?.last_name].filter(Boolean).join(' ').trim();
    return name || '—';
  }

  /** Platform column: `campaign_tracking_events.platform`. */
  getPlatformLabel(event: CampaignTrackingEvent): string {
    const platform = event.platform?.trim();
    if (!platform) {
      return '—';
    }
    return this.formatSourceLabel(platform);
  }

  getEventType(event: CampaignTrackingEvent): string {
    return event.event_type?.trim() || '';
  }

  getStatusLabel(eventType: string): string {
    return this.getEventTypeDisplayLabel(eventType);
  }

  getFilterEventTypeLabel(eventType: string): string {
    return this.getEventTypeDisplayLabel(eventType);
  }

  getStatusClass(eventType: string): string {
    const map: Record<string, string> = {
      opened: 'status-opened',
      clicked: 'status-clicked',
      sent: 'status-sent',
      delivered: 'status-delivered',
      impression: 'status-impression',
      conversion: 'status-redeemed',
      failed: 'status-failed',
      otp_send: 'status-otp-send',
      offer_received: 'status-received',
    };
    return map[eventType] ?? 'status-default';
  }

  getPaymentTime(event: CampaignTrackingEvent): string {
    const topLevel = event.payment_time?.trim();
    if (topLevel) {
      return this.formatDateTime(topLevel);
    }
    const metadata = event.metadata as { payment_time?: string } | null;
    const nested = metadata?.payment_time?.trim();
    if (nested) {
      return this.formatDateTime(nested);
    }
    return '---';
  }

  formatDateTime(dateString?: string | null): string {
    if (!dateString?.trim()) {
      return '—';
    }

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }

    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  trackByLeadId(_: number, lead: CampaignTrackingEvent): string {
    return lead.id;
  }

  trackByIndex(index: number): number {
    return index;
  }

  /** Builds the tracking-events API `event_type` query param from selected filters. */
  private getApiEventTypeParam(): string {
    if (!this.filters.eventTypes.length) {
      return this.defaultLeadEventTypes.join(',');
    }

    return this.filters.eventTypes.join(',');
  }

  private applyEventTypeFilter(events: CampaignTrackingEvent[]): CampaignTrackingEvent[] {
    if (!this.filters.eventTypes.length) {
      return events.filter(event => event.event_type !== 'failed');
    }
    return events.filter(event => this.filters.eventTypes.includes(event.event_type));
  }

  private applySearchFilter(): void {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      this.filteredLeads = [...this.leads];
      return;
    }

    this.filteredLeads = this.leads.filter(event => {
      const customer = event.customer;
      const haystack = [
        customer?.contact,
        customer?.email,
        customer?.first_name,
        customer?.last_name,
        event.platform,
        event.event_type,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }

  private buildExportFilename(): string {
    const normalized = (this.campaign?.name || 'campaign').trim().toLowerCase();
    const chars: string[] = [];
    let pendingDash = false;
    for (const char of normalized) {
      const isAlphaNum = (char >= 'a' && char <= 'z') || (char >= '0' && char <= '9');
      if (isAlphaNum) {
        if (pendingDash && chars.length > 0) {
          chars.push('-');
        }
        chars.push(char);
        pendingDash = false;
        continue;
      }
      pendingDash = true;
    }
    const slug = chars.join('').slice(0, 50) || 'campaign';
    const date = new Date().toISOString().slice(0, 10);
    return `${slug}-leads-${date}.xlsx`;
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 100);
  }

  private formatPhoneNumber(value: string): string {
    return PhoneValidator.formatUsaPhoneDisplay(value);
  }

  private formatSourceLabel(value: string): string {
    const normalized = value.trim().toLowerCase();
    const map: Record<string, string> = {
      email: 'Email',
      sms: 'SMS',
      rcs: 'RCS',
      facebook: 'Facebook',
      google: 'Google Ads',
    };
    return map[normalized] ?? this.capitalize(normalized);
  }

  private getEventTypeDisplayLabel(eventType: string): string {
    const map: Record<string, string> = {
      offer_received: 'Received',
      conversion: 'Redeemed',
    };
    return map[eventType] ?? this.formatEventTypeLabel(eventType);
  }

  private formatEventTypeLabel(value: string): string {
    if (!value) {
      return '';
    }

    return value
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private capitalize(value: string): string {
    if (!value) {
      return '';
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
}
